'use client'

import React, { Suspense, useEffect, useState } from 'react'
import { Error } from '../Error'
import { Width } from '../Width'
import { Controller } from 'react-hook-form'

import { getCollectionAction, type MultiCheckCollection } from '@/app/actions/collection'
import DynamicIcon, { IconName } from '@/components/DynamicIcon'
import Pill from '@/components/Pill'
import { Checkbox as CheckboxUi } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { cn } from '@/utilities/ui'

import type { Control, FieldErrorsImpl, FieldValues } from 'react-hook-form'

type ItemDoc = {
  id: number
  title: string
  icon?: string | null
  color?: string | null
}

export const MultiCheck: React.FC<{
  name: string
  label: string
  collection: MultiCheckCollection
  required?: boolean
  max?: number
  errors: Partial<FieldErrorsImpl>
  control: Control<FieldValues>
}> = ({ name, collection, required, max, errors, control }) => {
  const [itemList, setItemList] = useState<ItemDoc[]>([])
  const [loading, setLoading] = useState(true)
  const usePills = collection === 'topics'

  useEffect(() => {
    let cancelled = false

    getCollectionAction({ collection })
      .then((data) => {
        if (!cancelled) {
          setItemList(Array.isArray(data.data) ? data.data : [])
        }
      })
      .catch(() => {
        if (!cancelled) setItemList([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [collection])

  return (
    <Width>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading topics…</p>
      ) : (
        <Controller
          control={control}
          name={name}
          defaultValue={[]}
          rules={{
            validate: (value) => {
              const selected = Array.isArray(value) ? value : []
              if (required && selected.length < 1) return 'Select at least one topic'
              if (max != null && selected.length > max) {
                return `Select at most ${max} topics`
              }
              return true
            },
          }}
          render={({ field: { onChange, value } }) => {
            const selectedTitles = Array.isArray(value) ? value : []
            const toggle = (title: string) => {
              if (selectedTitles.includes(title)) {
                onChange(selectedTitles.filter((x) => x !== title))
                return
              }
              if (max != null && selectedTitles.length >= max) return
              onChange([...selectedTitles, title])
            }

            if (usePills) {
              return (
                <Suspense fallback={null}>
                  <div>
                    <div className="flex flex-wrap gap-2">
                      {itemList.map((item) => {
                        const selected = selectedTitles.includes(item.title)
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => toggle(item.title)}
                            className={cn(
                              'cursor-pointer border-2 border-transparent',
                              selected && 'rounded-full border-evergreen-500',
                            )}
                          >
                            <Pill color={item.color ?? 'accent-foreground-3'}>
                              <DynamicIcon name={item.icon as IconName} className="size-4" />
                              {item.title ?? `Item #${item.id}`}
                            </Pill>
                          </button>
                        )
                      })}
                      {itemList.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          No {collection} items available.
                        </p>
                      )}
                    </div>
                  </div>
                </Suspense>
              )
            }

            return (
              <div>
                <div className="grid grid-cols-2 gap-4">
                  {itemList.map((item) => (
                    <div key={item.id} className="flex w-full items-center gap-2">
                      <CheckboxUi
                        id={`${name}-${item.id}`}
                        checked={selectedTitles.includes(item.title)}
                        onCheckedChange={() => toggle(item.title)}
                      />
                      <Label htmlFor={`${name}-${item.id}`} className="cursor-pointer font-normal">
                        {item.title ?? `Item #${item.id}`}
                      </Label>
                    </div>
                  ))}
                  {itemList.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No {collection} items available.
                    </p>
                  )}
                </div>
              </div>
            )
          }}
        />
      )}
      {errors[name] && <Error name={name} />}
    </Width>
  )
}
